import React, { useEffect, useState } from 'react'
import {
  Collapse,
  Checkbox,
  Row,
  Col,
  message,
  FormInstance,
  Alert
} from 'antd'
import { GET_PRODUCTS_CATEGORIES_BY_CATEGORY_IDS } from './LoyaltyQueries'
import { groupBy, flatten, every } from 'lodash'
import { useQuery } from '@apollo/client'
import { Category } from './LoyaltyCardForm'
import Loading from 'components/Utils/Loading'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import { LoyaltyRewardType } from './types'

interface ProductVariant {
  id: string
  name: string
}

interface ProductsCategories {
  category_id: string
  product: {
    product_variants: {
      id: string
      name: string
    }[]
  }
}

interface ProductVariantGroup {
  name: string
  product_variants: ProductVariant[]
}

type Props = {
  categories: Category[] | undefined
  selectedProductVariantIds: string[]
  form: FormInstance
  isLoadingCategoriesData: boolean
} & (LoyaltyProps | RewardsProps)

type LoyaltyProps = {
  formType: 'loyalty'
  index?: number
}

// Required on rewards form
type RewardsProps = {
  formType: 'rewards'
  index: number
}

type ProductVariantGroups = Record<string, ProductVariantGroup>

const { Panel } = Collapse

const ProductPanels = ({
  categories,
  selectedProductVariantIds,
  form,
  formType,
  index = 0,
  isLoadingCategoriesData
}: Props) => {
  const [productVariantGroups, setProductVariantGroups] = useState<
    ProductVariantGroups | undefined
  >({})
  const [selectedItems, setSelectedItems] = useState<Record<string, string[]>>(
    {}
  )
  const categoriesWithActiveProducts = categories?.filter((category) =>
    Boolean(category.products.length)
  )

  const {
    loading: isLoadingProductsCategories,
    error: errorProductsCategories
  } = useQuery<{
    products_categories: ProductsCategories[]
  }>(GET_PRODUCTS_CATEGORIES_BY_CATEGORY_IDS, {
    variables: {
      categoryIds: categoriesWithActiveProducts?.map((category) => ({
        category_id: { _eq: category.id }
      }))
    },
    skip: !categoriesWithActiveProducts?.length,
    onCompleted: (data) => {
      if (!data) return

      const { products_categories } = data

      const groupedItems = groupBy(
        products_categories,
        (item) => item.category_id
      )

      const groups: ProductVariantGroups =
        categoriesWithActiveProducts?.reduce((acc, category) => {
          const productVariants = flatten(
            groupedItems[category.id]?.map(
              (group) => group.product.product_variants
            )
          )

          const sortedProductVariants = productVariants.sort((a, b) => {
            const aName = a.name.toLowerCase()
            const bName = b.name.toLowerCase()
            if (aName < bName) {
              return -1
            }
            if (aName > bName) {
              return 1
            }
            return 0
          })

          return {
            ...acc,
            [category.id]: {
              name: category.name,
              product_variants: sortedProductVariants
            }
          }
        }, {}) ?? {}

      const prefillSelectedItems = Object.keys(groups).reduce(
        (acc, current: string) => {
          const involvedIds = groups[current].product_variants
            .filter((product_variant) =>
              selectedProductVariantIds.some((id) => id === product_variant.id)
            )
            .map((product_variant) => product_variant.id)

          return {
            ...acc,
            [current]: involvedIds
          }
        },
        {}
      )

      setSelectedItems(prefillSelectedItems)
      setProductVariantGroups(groups)
    },
    onError: (error) =>
      message.error(
        `Unable to load products_categories due to ${error.message}`,
        5
      )
  })

  const isGroupSelected = (
    group: ProductVariantGroup,
    activeProductVariants: string[]
  ) => {
    if (!group) return
    if (!activeProductVariants) return

    const groupIds = group.product_variants.map(
      (product_variants) => product_variants.id
    )
    return every(groupIds, (id) => activeProductVariants.includes(id))
  }

  const indeterminate = (
    group: ProductVariantGroup,
    activeProductVariants: string[]
  ) => {
    if (!group) return
    if (!activeProductVariants) return

    const groupIds = group.product_variants.map(
      (product_variant) => product_variant.id
    )
    const someSelected = activeProductVariants?.some((id) =>
      groupIds.includes(id)
    )

    return (
      !every(groupIds, (id) => activeProductVariants.includes(id)) &&
      someSelected
    )
  }

  const handleToggleSelectAll = (
    e: CheckboxChangeEvent,
    category: Category,
    productVariantGroups: ProductVariantGroups
  ) => {
    if (e.target.checked) {
      const newIds = productVariantGroups[category.id].product_variants.map(
        (product_variant) => product_variant.id
      )

      const updatedSelectedItems = Object.entries(selectedItems).reduce(
        (acc, [key, value]) => {
          const groupProductVariants =
            productVariantGroups[key].product_variants
          const isInvolved = productVariantGroups[key].product_variants.some(
            ({ id }) => newIds.includes(id)
          )

          const involvedIds = newIds.filter((product_variant_id) =>
            groupProductVariants.some(({ id }) => id === product_variant_id)
          )

          if (isInvolved) {
            const combined = [...value, ...involvedIds]

            return {
              ...acc,
              [key]: [...new Set(combined)]
            }
          } else {
            return {
              ...acc,
              [key]: value
            }
          }
        },
        {}
      )

      setSelectedItems(updatedSelectedItems)
    } else {
      const newIds = productVariantGroups[category.id].product_variants.map(
        (product_variant) => product_variant.id
      )

      const updatedSelectedItems = Object.entries(selectedItems).reduce(
        (acc, [key, value]) => {
          const groupProductVariants =
            productVariantGroups[key].product_variants
          const isInvolved = productVariantGroups[key].product_variants.some(
            ({ id }) => newIds.includes(id)
          )

          const involvedIds = newIds.filter((product_variant_id) =>
            groupProductVariants.some(({ id }) => id === product_variant_id)
          )

          if (isInvolved) {
            const filtered = value.filter(
              (id) => !involvedIds.some((pid) => pid === id)
            )

            return {
              ...acc,
              [key]: [...new Set(filtered)]
            }
          } else {
            return {
              ...acc,
              [key]: value
            }
          }
        },
        {}
      )

      setSelectedItems(updatedSelectedItems)
    }
  }

  const handleToggleSelectOne = (
    e: CheckboxChangeEvent,
    product_variant: ProductVariant,
    productVariantGroups: ProductVariantGroups
  ) => {
    if (e.target.checked) {
      const updatedSelectedItems = Object.entries(selectedItems).reduce(
        (acc, [key, value]) => {
          const involvedGroup = productVariantGroups[key].product_variants.some(
            ({ id }) => id === product_variant.id
          )

          if (involvedGroup) {
            const combined = [...value, product_variant.id]

            return {
              ...acc,
              [key]: [...new Set(combined)]
            }
          } else {
            return {
              ...acc,
              [key]: value
            }
          }
        },
        {}
      )

      setSelectedItems(updatedSelectedItems)
    } else {
      const updatedSelectedItems = Object.entries(selectedItems).reduce(
        (acc, [key, value]) => {
          const involvedGroup = productVariantGroups[key].product_variants.some(
            ({ id }) => id === product_variant.id
          )

          if (involvedGroup) {
            const filtered = value.filter((id) => id !== product_variant.id)

            return {
              ...acc,
              [key]: [...new Set(filtered)]
            }
          } else {
            return {
              ...acc,
              [key]: value
            }
          }
        },
        {}
      )

      setSelectedItems(updatedSelectedItems)
    }
  }

  useEffect(() => {
    const values = flatten(Object.values(selectedItems))
    const productVariantUUIDs = [...new Set(values)]

    switch (formType) {
      case 'loyalty':
        form?.setFieldsValue({
          product_variant_ids: productVariantUUIDs
        })
        break

      case 'rewards':
        const rewards = form.getFieldValue('rewards')
        const currentReward = rewards[index]

        form.setFieldsValue({
          rewards: rewards.map((reward: LoyaltyRewardType) => {
            if (reward.id === currentReward.id) {
              return {
                ...reward,
                product_variant_ids: Boolean(productVariantUUIDs.length)
                  ? productVariantUUIDs
                  : currentReward['product_variant_ids']
              }
            } else {
              return reward
            }
          })
        })

        break

      default:
        return
    }
  }, [selectedItems, formType])

  if (isLoadingProductsCategories || isLoadingCategoriesData) return <Loading />
  if (!productVariantGroups)
    return (
      <Alert
        message='Missing product variants data'
        type='error'
        showIcon
      ></Alert>
    )
  if (errorProductsCategories)
    return (
      <Alert
        message={`Unable to load products categories data due to: ${errorProductsCategories.message}`}
        type='error'
        showIcon
      ></Alert>
    )

  return (
    <Collapse style={{ width: '100%' }}>
      {categoriesWithActiveProducts?.map((category) => {
        return (
          <Panel
            key={category.id}
            header={category.name}
            extra={
              <span onClick={(e) => e.stopPropagation()}>
                <label htmlFor={`select-all-${category.id}-${category.name}`}>
                  Select All
                </label>
                <Checkbox
                  id={`select-all-${category.id}-${category.name}`}
                  className='_ml-8'
                  onChange={(e) =>
                    handleToggleSelectAll(e, category, productVariantGroups)
                  }
                  checked={isGroupSelected(
                    productVariantGroups[category.id],
                    selectedItems[category.id]
                  )}
                  indeterminate={indeterminate(
                    productVariantGroups[category.id],
                    selectedItems[category.id]
                  )}
                />
              </span>
            }
          >
            <div style={{ padding: '16px 16px 0 16px' }}>
              {productVariantGroups[category.id]?.product_variants.map(
                (product_variant) => {
                  return (
                    <Row justify='space-between' className='_mb-16'>
                      <Col span={23}>
                        <label htmlFor={`${category.id}-${product_variant.id}`}>
                          {product_variant.name}
                        </label>
                      </Col>
                      <Col span={1}>
                        <Checkbox
                          id={`${category.id}-${product_variant.id}`}
                          checked={selectedItems[category.id]?.includes(
                            product_variant.id
                          )}
                          onChange={(e) =>
                            handleToggleSelectOne(
                              e,
                              product_variant,
                              productVariantGroups
                            )
                          }
                        />
                      </Col>
                    </Row>
                  )
                }
              )}
            </div>
          </Panel>
        )
      })}
    </Collapse>
  )
}

export default ProductPanels
